import React from "react";
//#region types
import { FormEnums } from "models/enums";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { AutoCompleteExtraProps, GenericFormSection } from "models/propTypes/genericFormProps";
import { QuotesFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//#region components
import { useCompanyService, useCustomerService, useQuotesService } from "serviceHooks";
import { useCustomerSearch } from "components/customer";
import { useInvoicePermissions } from "components/invoice";
//#endregion
import { FilterHelper, QueryStateHelper } from "utils/helpers";

function useQuotesReportFilterDefinition() {
  const customerService = useCustomerService();
  const companyService = useCompanyService();
  const quotesService = useQuotesService();

  const [suggestedCustomers, setSuggestedCustomers] = React.useState<AutocompleteListItem[]>([]);
  const searchCustomers = useCustomerSearch();

  const { getIsInvoiceAdmin } = useInvoicePermissions();

  React.useEffect(() => {
    customerService
      .getCustomerSuggestions()
      .then((res) =>
        setSuggestedCustomers(res.map((customer: any) => ({ id: customer.id ?? 0, value: customer.name ?? "" })))
      );
  }, []);

  const apiToReportFilterFormatter = async (filter: WintSuperkollAPIModel.QuotationsApiFilter) => {
    const reportFilter: QuotesFormTypes.Filter = {};

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Quote.Filter.SerialNumber,
      filter.serialNumberStart,
      filter.serialNumberEnd
    );

    let ownerName: Promise<string> | undefined;
    if (filter.ownerId !== undefined) {
      reportFilter.ownerId = filter.ownerId;
      ownerName = companyService.getEmployeeName(filter.ownerId);
    }

    FilterHelper.setDateRange(reportFilter, FormFieldConstants.Quote.Filter.Date, filter.fromDate, filter.toDate);

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.Quote.Filter.EndDate,
      filter.fromEndDate,
      filter.toEndDate
    );

    let customerName: Promise<string> | undefined;
    if (filter.customerId !== undefined) {
      customerName = customerService.getCustomer(filter.customerId).then((c) => c.name ?? "");
    }

    let states = filter.states?.map((s) => ({ id: s, value: quotesService.getStateText(s) })) ?? [];
    if (filter.quotationState !== undefined) {
      states = [...states, { id: filter.quotationState, value: quotesService.getStateText(filter.quotationState) }];
    }

    if (states.length > 0) {
      reportFilter.states = states;
    }

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Quote.Filter.Amount,
      filter.minTotalAmount,
      filter.maxTotalAmount
    );

    if (ownerName) {
      reportFilter.ownerName = await ownerName;
    }

    if (customerName) {
      reportFilter.customerName = await customerName;
    }

    return reportFilter;
  };

  const filterFormFields: GenericFormSection<QuotesFormTypes.Filter>[] = [
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        serialNumber: {
          label: TranslationKeys.Common.Number,
          type: FormEnums.FormFieldType.NUMBERRANGE,
        },
        ownerName: {
          label: TranslationKeys.Common.Owner,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          extraProps: {
            referenceFieldName: FormFieldConstants.Quote.Filter.OwnerId,
            items: (searchStr: string) => companyService.SearchPersonsAutoComplete(searchStr, false),
          } as AutoCompleteExtraProps<QuotesFormTypes.Filter>,
          hidden: !getIsInvoiceAdmin(),
        },
        date: {
          label: TranslationKeys.Common.Date,
          type: FormEnums.FormFieldType.DATERANGE,
        },
        endDate: {
          label: TranslationKeys.Common.ExpiryDate,
          type: FormEnums.FormFieldType.DATERANGE,
        },
        customerName: {
          label: TranslationKeys.Common.Customer,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          extraProps: {
            referenceFieldName: FormFieldConstants.Quote.Filter.CustomerId,
            suggestedItems: suggestedCustomers,
            items: searchCustomers,
          } as AutoCompleteExtraProps<QuotesFormTypes.Filter>,
        },
        states: {
          label: TranslationKeys.Common.Status,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: quotesService.getStates(),
          },
        },
        amount: {
          label: TranslationKeys.Common.Amount,
          type: FormEnums.FormFieldType.NUMBERRANGE,
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<QuotesFormTypes.Filter> = {
    serialNumber: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.Quote.Filter.SerialNumber),
    ownerId: QueryStateHelper.numberQueryEncoder(FormFieldConstants.Quote.Filter.OwnerId),
    ownerName: QueryStateHelper.stringQueryEncoder(FormFieldConstants.Quote.Filter.OwnerName),
    date: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.Quote.Filter.Date),
    endDate: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.Quote.Filter.EndDate),
    customerId: QueryStateHelper.numberQueryEncoder(FormFieldConstants.Quote.Filter.CustomerId),
    customerName: QueryStateHelper.stringQueryEncoder(FormFieldConstants.Quote.Filter.CustomerName),
    amount: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.Quote.Filter.Amount),
    states: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.Quote.Filter.States,
      quotesService.getStates()
    ),
  };

  return { filterFormFields, filterQueryStateHandler, apiToReportFilterFormatter };
}

export default useQuotesReportFilterDefinition;
